import {usePlantConfig} from '@domain/shifthandover'
import {RcfaLinks} from '@hconnect/common/components/runningTimes'
import {
  DateRange,
  EquipmentData,
  EquipmentRunningTimesDto as EquipmentRunningTimes
} from '@hconnect/common/types'
import {getSystemTimezone} from '@hconnect/common/utils'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {FiberManualRecord as Circle} from '@mui/icons-material'
import {Box, Grid, Typography, Theme} from '@mui/material'
import {
  OperationHoursLegendTooltip,
  getRunningTimesWithGap,
  getEquipmentStoppageCodesByStoppageType,
  StackedBarChart,
  EquipmentRunningTimesResultLegend,
  isKilnRunning
} from '@widgets/running-times'
import {last, get} from 'lodash'
import moment from 'moment-timezone'
import React, {ReactNode, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useTranslationPrefix} from '../hooks/useTranslationPrefix'

const statusStyle = {display: 'flex', alignItems: 'center'}

export type Props = {
  title?: string
  icon?: ReactNode
  equipmentRunningTimes?: EquipmentRunningTimes[]
  startDate: string
  endDate: string
  plantId: string
  noCard?: boolean
  noChartsSeparator?: boolean
  onClick?: (equipmentId: string) => void
  'data-test-id'?: string
  hideLegend?: boolean
}

export const OperationHoursTable: React.FC<Props> = (props) => {
  const {t, i18n} = useTranslation()
  const {performancePrefix} = useTranslationPrefix()
  const {data: config} = usePlantConfig({plantId: props.plantId, language: i18n.language})
  const timezone = useUpmPlantTimezone(props.plantId)
  const timeRange = useMemo<DateRange>(
    () => ({
      startDate: moment.utc(props.startDate).tz(timezone),
      endDate: moment.utc(props.endDate).tz(timezone)
    }),
    [props.startDate, props.endDate, timezone]
  )
  const stoppageCodesByType = useMemo(
    () => getEquipmentStoppageCodesByStoppageType(config),
    [config]
  )

  const operationData = props.equipmentRunningTimes ?? []

  const operationHoursTable = (
    <>
      <Box display="flex" alignItems="center">
        {props.title && (
          <Typography variant="h3" display="flex" alignItems="center">
            {props.icon}
            {props.title}
          </Typography>
        )}
        {!props.hideLegend && <OperationHoursLegendTooltip />}
      </Box>

      {operationData.map(({equipment, runningTimes, totalStopped, totalRunning}, index) => {
        // Note: Last element of the running times gives the currently active status
        const activeStatus = get(last(runningTimes), 'runningTimeType')

        return (
          <Box
            key={equipment?.id}
            sx={(theme: Theme) => ({
              '&:not(:last-of-type)': {
                borderBottom: props.noChartsSeparator
                  ? 'none'
                  : `1px solid ${theme.palette.grey.A200}`,
                padding: theme.spacing(2, 0)
              },
              '&:last-of-type': {
                paddingTop: theme.spacing(2)
              },
              cursor: props.onClick ? 'pointer' : 'unset'
            })}
            onClick={equipment?.id ? () => props.onClick?.(equipment.id) : undefined}
            data-test-id={`operation-hour-row-${index}`}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex">
                <Typography mr={2}>{equipment?.text}</Typography>

                {activeStatus && isKilnRunning(activeStatus) && (
                  <Typography
                    mr={2}
                    variant="caption"
                    sx={(theme: Theme) => ({
                      ...statusStyle,
                      color: theme.palette.success.light
                    })}
                  >
                    <Circle
                      sx={(theme: Theme) => ({
                        color: theme.palette.success.light,
                        verticalAlign: 'text-bottom'
                      })}
                    />
                    {t(`${performancePrefix}.runningTime.label.running`)}
                  </Typography>
                )}
                {activeStatus && !isKilnRunning(activeStatus) && (
                  <Typography
                    mr={2}
                    variant="caption"
                    sx={(theme: Theme) => ({
                      ...statusStyle,
                      color: theme.palette.error.light
                    })}
                  >
                    <Circle
                      sx={(theme: Theme) => ({
                        color: theme.palette.error.light,
                        verticalAlign: 'text-bottom'
                      })}
                    />
                    {t(`${performancePrefix}.runningTime.label.off`)}
                  </Typography>
                )}
              </Box>
              <EquipmentRunningTimesResultLegend
                totalRunning={totalRunning}
                totalStopped={totalStopped}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                flexWrap: 'wrap'
              }}
            >
              {!runningTimes ? (
                <Box>{t(`${performancePrefix}.plantStatus.label.noData`)}</Box>
              ) : config ? (
                <StackedBarChart
                  timeRange={timeRange}
                  data={getRunningTimesWithGap(
                    runningTimes.filter((rt) => rt.begin !== rt.end),
                    timeRange,
                    getSystemTimezone()
                  )}
                  stoppageCodesByType={stoppageCodesByType}
                  equipment={equipment as EquipmentData}
                  renderTooltipFooter={({rootCauseFailureAnalyses}) =>
                    rootCauseFailureAnalyses && (
                      <RcfaLinks
                        plantId={props.plantId}
                        rootCauseFailureAnalyses={rootCauseFailureAnalyses}
                      />
                    )
                  }
                />
              ) : null}
            </Box>
          </Box>
        )
      })}
    </>
  )

  if (props.noCard) {
    return operationHoursTable
  }

  return (
    <Grid data-test-id={props['data-test-id']}>
      <CardBox>{operationHoursTable}</CardBox>
    </Grid>
  )
}
